import { useEffect, useMemo, useRef, useState } from 'react';
import type { Book, Rendition } from 'epubjs';
import {
  LayoutMode,
  PageMargin,
} from '../components/reader/types';

const SPREAD_BREAKPOINT_PX = 900;
const RESIZE_DEBOUNCE_MS = 200;

interface UseEpubLayoutArgs {
  book: Book | null;
  rendition: Rendition | null;
  /** Current CFI so we can re-display after mode changes without losing position. */
  currentCFI: string;
  /** Subset of ReaderSettings — only the layout-relevant fields. */
  layoutMode: LayoutMode;
  pageMargin: PageMargin;
  /** Whether book.locations has finished generating. */
  locationsReady: boolean;
  totalLocations: number;
  currentLocationIndex: number;
  /**
   * End-of-visible-range location index reported by epub.js on relocated events.
   * Equals currentLocationIndex in Single / Scrolling modes; in Spread mode this
   * is typically currentLocationIndex + 1 (two pages visible at once), and the
   * consumer (bottom toolbar) renders a "6–7 / 815" range instead of just "6".
   */
  currentEndLocationIndex?: number;
}

export interface EpubLayoutResult {
  /**
   * The actually-applied mode after Auto resolution and narrow-screen fallback.
   * Always one of Single/Spread/Scrolling — never Auto.
   */
  effectiveMode: LayoutMode.Single | LayoutMode.Spread | LayoutMode.Scrolling;
  /** Viewport is too narrow to honour an explicit Spread choice. */
  forcedSingleFromSpread: boolean;
  /** 1-based start page of the visible range, or undefined while locations are generating. */
  currentPage?: number;
  /** 1-based end page of the visible range. Equals currentPage except in Spread mode. */
  currentEndPage?: number;
  /** Total pages, or undefined while locations are generating. */
  totalPages?: number;
}

function resolveMode(
  layoutMode: LayoutMode,
  viewportWidth: number
): { effective: LayoutMode.Single | LayoutMode.Spread | LayoutMode.Scrolling; forcedSingle: boolean } {
  if (layoutMode === LayoutMode.Scrolling) {
    return { effective: LayoutMode.Scrolling, forcedSingle: false };
  }
  if (layoutMode === LayoutMode.Single) {
    return { effective: LayoutMode.Single, forcedSingle: false };
  }
  if (layoutMode === LayoutMode.Spread) {
    if (viewportWidth >= SPREAD_BREAKPOINT_PX) {
      return { effective: LayoutMode.Spread, forcedSingle: false };
    }
    return { effective: LayoutMode.Single, forcedSingle: true };
  }
  // Auto
  return {
    effective: viewportWidth >= SPREAD_BREAKPOINT_PX ? LayoutMode.Spread : LayoutMode.Single,
    forcedSingle: false,
  };
}

function marginPx(margin: PageMargin): string {
  switch (margin) {
    case PageMargin.Narrow: return '20px';
    case PageMargin.Wide: return '80px';
    case PageMargin.Medium:
    default: return '40px';
  }
}

export function useEpubLayout(args: UseEpubLayoutArgs): EpubLayoutResult {
  const {
    rendition,
    currentCFI,
    layoutMode,
    pageMargin,
    locationsReady,
    totalLocations,
    currentLocationIndex,
    currentEndLocationIndex,
  } = args;

  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1024 : window.innerWidth
  );

  // Debounced resize listener — re-evaluates Auto mode on window resize / rotation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: number | undefined;
    const onResize = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setViewportWidth(window.innerWidth);
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const { effective: effectiveMode, forcedSingle: forcedSingleFromSpread } = useMemo(
    () => resolveMode(layoutMode, viewportWidth),
    [layoutMode, viewportWidth]
  );

  // Re-apply flow/spread/margin whenever effectiveMode or margin changes.
  const lastAppliedRef = useRef<{ mode: LayoutMode; margin: PageMargin } | null>(null);

  useEffect(() => {
    if (!rendition) return;

    const last = lastAppliedRef.current;
    const modeChanged = !last || last.mode !== effectiveMode;
    const marginChanged = !last || last.margin !== pageMargin;
    if (!modeChanged && !marginChanged) return;

    // flow + spread
    if (effectiveMode === LayoutMode.Scrolling) {
      rendition.flow('scrolled-doc');
      rendition.spread('none');
    } else if (effectiveMode === LayoutMode.Spread) {
      rendition.flow('paginated');
      // Our resolver already decided the viewport is wide enough (>= SPREAD_BREAKPOINT_PX).
      // Use 'both' to force spread — epub.js's 'auto' uses min-per-page semantics and
      // would require 2 * min viewport width, which is too restrictive on 1280px screens.
      rendition.spread('both');
    } else {
      // Single
      rendition.flow('paginated');
      rendition.spread('none');
    }

    // margins — inject a theme override
    const px = marginPx(pageMargin);
    try {
      rendition.themes.override('padding', px, true);
    } catch {
      // themes.override may throw before first display; safe to swallow.
    }

    // Re-display current position so layout change doesn't throw user back to start.
    if (currentCFI) {
      rendition.display(currentCFI).catch(() => {
        // If the CFI is invalid (book still initializing), ignore — initial display will handle it.
      });
    }

    lastAppliedRef.current = { mode: effectiveMode, margin: pageMargin };
  }, [rendition, effectiveMode, pageMargin, currentCFI]);

  // Page indicators — derived from book.locations + currentLocationIndex.
  const currentPage = locationsReady && totalLocations > 0 ? currentLocationIndex + 1 : undefined;
  // End-of-visible-range marker. Equals currentPage for Single / Scrolling;
  // in Spread mode it's typically currentPage + 1 so the bottom toolbar can
  // render "6–7 / 815" instead of a single number.
  const currentEndPage = locationsReady && totalLocations > 0 && currentEndLocationIndex != null
    ? Math.max(currentLocationIndex, currentEndLocationIndex) + 1
    : undefined;
  const totalPages = locationsReady && totalLocations > 0 ? totalLocations : undefined;

  return {
    effectiveMode,
    forcedSingleFromSpread,
    currentPage,
    currentEndPage,
    totalPages,
  };
}
